// Considered: AddChannelDialogOrganism — rejected because it is channel-specific and cannot be repurposed for metaspace selection. See spec Reuse Audit.
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import './AddMetaspaceDialogOrganism.scss';

export interface AddMetaspaceDialogOrganismProps {
  open: boolean;
  sphereId: string;
  sphereSchemaSystemName?: string;
  onClose: () => void;
  onAdded: () => void;
}

interface MetaspaceOption {
  metaspace_id: string;
  system_name: string;
  name: string;
  description: string;
}

const AddMetaspaceDialogOrganism: React.FC<AddMetaspaceDialogOrganismProps> = ({
  open,
  sphereId,
  sphereSchemaSystemName,
  onClose,
  onAdded,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [searchTerm, setSearchTerm] = useState('');
  const [metaspaces, setMetaspaces] = useState<MetaspaceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSystemName, setSelectedSystemName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchMetaspaces = useCallback(async () => {
    if (!apiClients?.schemaClient) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sphereSchemaSystemName) {
        params.set('applicableTo', sphereSchemaSystemName);
      }
      const res = await apiClients.schemaClient.get<{ metaspaces: MetaspaceOption[] }>(
        `/metaspaces?${params.toString()}`
      );
      setMetaspaces(res.metaspaces ?? []);
    } catch {
      setMetaspaces([]);
    } finally {
      setLoading(false);
    }
  }, [apiClients, sphereSchemaSystemName]);

  useEffect(() => {
    if (open) {
      fetchMetaspaces();
      setSearchTerm('');
      setSelectedSystemName(null);
      setError(null);
      setSubmitting(false);
    }
  }, [open, fetchMetaspaces]);

  useEffect(() => {
    if (open && !loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, loading]);

  const filteredMetaspaces = metaspaces.filter((m) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      m.name.toLowerCase().includes(term) ||
      m.description?.toLowerCase().includes(term)
    );
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!apiClients?.graphClient || !selectedSystemName) return;

      setSubmitting(true);
      setError(null);

      try {
        await apiClients.graphClient.post(
          `/spheres/${encodeURIComponent(sphereId)}/metaspaces`,
          { metaspaceSystemName: selectedSystemName }
        );
        onAdded();
        onClose();
      } catch (err: unknown) {
        const status =
          err instanceof Error && 'response' in (err as any)
            ? (err as any).response?.status
            : null;
        if (status === 409) {
          setError(t('sphereMetaspaces.addMetaspace.errorDuplicate'));
        } else if (status === 400) {
          setError(t('sphereMetaspaces.addMetaspace.errorApplicability'));
        } else {
          setError(t('sphereMetaspaces.addMetaspace.error'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [apiClients, selectedSystemName, sphereId, onAdded, onClose, t]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) handleClose();
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  if (!open) return null;

  return (
    <div
      className="marketplace-dialog-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="marketplace-dialog marketplace-add-metaspace-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-metaspace-dialog-title"
      >
        <div className="marketplace-dialog__header">
          <h2 id="add-metaspace-dialog-title" className="marketplace-dialog__title">
            {t('sphereMetaspaces.addMetaspace.title')}
          </h2>
          <button
            type="button"
            className="marketplace-dialog__close-btn"
            onClick={handleClose}
            aria-label={t('common.actions.close')}
          >
            &times;
          </button>
        </div>

        <form className="marketplace-dialog__body" onSubmit={handleSubmit}>
          <div className="marketplace-add-metaspace-dialog__search">
            <i className="pi pi-search marketplace-add-metaspace-dialog__search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="marketplace-dialog__input marketplace-add-metaspace-dialog__search-input"
              placeholder={t('sphereMetaspaces.addMetaspace.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="marketplace-add-metaspace-dialog__list">
            {loading && (
              <div className="marketplace-add-metaspace-dialog__loading">
                <i className="pi pi-spin pi-spinner" />
              </div>
            )}

            {!loading && filteredMetaspaces.length === 0 && (
              <div className="marketplace-add-metaspace-dialog__empty">
                {t('sphereMetaspaces.addMetaspace.noResults')}
              </div>
            )}

            {!loading &&
              filteredMetaspaces.map((m) => (
                <button
                  key={m.system_name}
                  type="button"
                  className={`marketplace-add-metaspace-dialog__item${
                    selectedSystemName === m.system_name
                      ? ' marketplace-add-metaspace-dialog__item--selected'
                      : ''
                  }`}
                  onClick={() => setSelectedSystemName(m.system_name)}
                >
                  <i className="metaspace-icon" />
                  <div className="marketplace-add-metaspace-dialog__item-info">
                    <span className="marketplace-add-metaspace-dialog__item-name">{m.name}</span>
                    {m.description && (
                      <span className="marketplace-add-metaspace-dialog__item-desc">
                        {m.description}
                      </span>
                    )}
                  </div>
                </button>
              ))}
          </div>

          {error && <div className="marketplace-dialog__error">{error}</div>}

          <div className="marketplace-dialog__actions">
            <button
              type="button"
              className="marketplace-dialog__cancel-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="marketplace-dialog__submit-btn"
              disabled={!selectedSystemName || submitting}
            >
              {submitting
                ? t('sphereMetaspaces.addMetaspace.adding')
                : t('sphereMetaspaces.addMetaspace.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMetaspaceDialogOrganism;
